import React, { useState } from "react";
import SidebarAdmin from "./SidebarAdmin";
import AdminNavbar from "./AdminNavbar";
import { Career } from "../Types/Types";
import AdminSearchBar from "../Assets/AdminSearchBar";
import PostAdminButton from "../Assets/PostAdminButton";
import useAdminCareer from "../Hooks/useAdminCareer";
import AdminCareerTable from "./AdminCareerTable";
import ModalConfirmation from "../Assets/ModalConfirmation";
import CareerModal from "./CareerModal";
import CareerEditModal from "./CareerEditModal";
import { useTranslation } from "react-i18next";

interface AdminCarrersProps {
    lang: string;
}


const AdminCarrers: React.FC<AdminCarrersProps> = ({ lang }) => {
    const { t } = useTranslation()
    const [modalOpen, setModalOpen] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const [selectedCareerId, setSelectedCareerId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false); // Toggle form visibility
    const [showEditModal, setShowEditModal] = useState(false);
    const [careerToEdit, setCareerToEdit] = useState<Career | null>(null);


    const {
        careers,
        loading,
        error,
        currentPage,
        setCurrentPage,
        totalCount,
        searchQuery,
        setSearchQuery,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        createCareer,
        updateCareer,
        deleteCareer,
        success,
        setSuccess
    } = useAdminCareer(itemsPerPage, "default", null);

    // Toggle modal visibility, and set item to edit if it's an edit action



    const handleSearch = (query: string) => {
        setSearchQuery(query); // Update the search query state
        setCurrentPage(1); // Optionally reset the page to 1 when search query changes
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setItemsPerPage(newRowsPerPage); // Dynamically update itemsPerPage state
        setCurrentPage(1); // Optionally reset the current page when rows per page change
    };




    const handleDeleteRequest = (id: string) => {
        setSelectedCareerId(id);
        setModalOpen(true);
    };

    // ✅ Confirm deletion
    const handleDeleteConfirm = () => {
        if (selectedCareerId) {
            deleteCareer(selectedCareerId);
            setModalOpen(false);
            setSelectedCareerId(null);
        }
    };

    const handleDeleteCancel = () => {
        setModalOpen(false);
        setSelectedCareerId(null);
    };





    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar */}
            <div className="w-80">
                <SidebarAdmin />
            </div>

            <div className="flex-1 flex flex-col">
                {/* Navbar */}
                <AdminNavbar />

                {/* Main Content */}
                <div className="flex-1 py-12 px-48 space-y-12 font-montserrat">
                    <h1 className="text-4xl font-bold text-brand-800">
                        {t(`admin.vacancy`)}
                    </h1>
                    <div className="flex justify-between items-center space-x-4">
                        <PostAdminButton onClick={() => setShowForm(true)} />
                        <AdminSearchBar searchQuery={searchQuery} onSearch={handleSearch} />
                    </div>
                    <AdminCareerTable
                        careers={careers}
                        loading={loading}
                        error={error}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        currentPage={currentPage}
                        totalCount={totalCount}
                        onSortChange={(field, order) => {
                            setSortBy(field);
                            setSortOrder(order);
                        }}
                        onPageChange={setCurrentPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        onDelete={handleDeleteRequest}
                        onEdit={(career) => {
                            setCareerToEdit(career);
                            setShowEditModal(true)
                        }}
                        selectedCareerId={selectedCareerId}
                        lang={lang}
                        success={success}
                        setSuccess={setSuccess}
                    />
                </div>

            </div>

            <CareerModal
                isOpen={showForm}
                onClose={() => setShowForm(false)}
                onSubmit={async (careerData) => {
                    await createCareer(careerData);
                    setShowForm(false);
                    setCurrentPage(1);
                }}
            />



            {showEditModal && careerToEdit && (
                <CareerEditModal
                    isOpen={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        setCareerToEdit(null);
                    }}
                    onSubmit={async (id, formData) => {
                        await updateCareer(id, formData);
                        setShowEditModal(false);
                        setCareerToEdit(null);
                    }}
                    initialData={careerToEdit}
                    lang={lang}
                />
            )}

            <ModalConfirmation
                isOpen={modalOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />
        </div>
    );
};

export default AdminCarrers;
